import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface RouterContextType {
  path: string;
  params: Record<string, string>;
  navigate: (to: string) => void;
}

const RouterContext = createContext<RouterContextType>({
  path: "/",
  params: {},
  navigate: () => {},
});

export function useRouter() {
  return useContext(RouterContext);
}

export function useParams() {
  return useContext(RouterContext).params;
}

function matchRoute(
  pattern: string,
  path: string
): Record<string, string> | null {
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = path.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(":")) {
      params[patternParts[i].slice(1)] = pathParts[i];
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  return params;
}

interface Route {
  pattern: string;
  component: React.ComponentType;
}

export function Router({ routes, fallback }: { routes: Route[]; fallback?: React.ComponentType }) {
  const [path, setPath] = useState(window.location.pathname);

  const navigate = useCallback((to: string) => {
    window.history.pushState(null, "", to);
    setPath(to);
  }, []);

  useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  let matched: { component: React.ComponentType; params: Record<string, string> } | null = null;

  for (const route of routes) {
    const params = matchRoute(route.pattern, path);
    if (params !== null) {
      matched = { component: route.component, params };
      break;
    }
    // Handle root path
    if (route.pattern === "/" && path === "/") {
      matched = { component: route.component, params: {} };
      break;
    }
  }

  if (!matched) {
    const Fallback = fallback || routes[0]?.component;
    if (Fallback) {
      matched = { component: Fallback, params: {} };
    } else {
      return null;
    }
  }

  const Component = matched.component;
  return (
    <RouterContext.Provider value={{ path, params: matched.params, navigate }}>
      <Component />
    </RouterContext.Provider>
  );
}

export function Link({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const { navigate } = useRouter();
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(href);
  };
  return (
    <a href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  );
}
